// contexts/voice/types.ts
// All types and interfaces for the Voice context

import { Product } from '../../components/ProductCard';

// Connection & Chat States
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'speaking' | 'listening';
export type ChatMode = 'voice' | 'text';
export type VadMode = 'standard' | 'gl31';

// Voice Character Configuration
export const GEMINI_VOICES = [
  { id: 'Aoede', name: 'Aoede', description: 'Breezy', flag: '🌊' },
  { id: 'Achird', name: 'Achird', description: 'Friendly', flag: '😊' },
  { id: 'Puck', name: 'Puck', description: 'Upbeat', flag: '⚡' },
  { id: 'Sulafat', name: 'Sulafat', description: 'Warm', flag: '☀️' },
  { id: 'Kore', name: 'Kore', description: 'Firm', flag: '💼' },
  { id: 'Callirrhoe', name: 'Callirrhoe', description: 'Easy-going', flag: '🌿' },
  { id: 'Charon', name: 'Charon', description: 'Informative', flag: '📚' },
  { id: 'Fenrir', name: 'Fenrir', description: 'Excitable', flag: '🔥' },
] as const;

export type GeminiVoiceId = typeof GEMINI_VOICES[number]['id'];

// Tracking Types
export interface TrackingEvent {
  timestamp: string;
  status: string;
  location: string;
}

export interface TrackingInfo {
  carrier: string;
  tracking_number: string;
  status: string;
  current_location: string;
  estimated_delivery?: string;
  estimated_delivery_end?: string;
  delivered_at?: string;
  tracking_url?: string;
  delay_reason?: string;
  events?: TrackingEvent[];
}

export interface OrderItem {
  product_id: string;
  name: string;
  quantity: number;
  price: number;
}

export interface TrackingData {
  orderId: string;
  tracking: TrackingInfo;
  items: OrderItem[];
}

// Pickup & Callback Types
export interface PickupConfirmationData {
  confirmationNumber: string;
  date: string;
  timeSlot: string;
  itemName: string;
  itemPrice?: number;
  address: string;
}

export interface CallbackConfirmationData {
  confirmationNumber: string;
  day: string;
  date: string;
  time: string;
  reason: string;
}

// Handover Types
export interface HandoverSummary {
  customer_email?: string;
  customer_intent?: string;
  orders_discussed?: string[];
  products_discussed?: string[];
  actions_taken?: string[];
  open_questions?: string;
  cart_items?: string;
  current_page?: string;
}

export interface AgentInfo {
  name: string;
  type: 'ai' | 'human';
  role: string;
}

export interface HandoverData {
  status: 'transferring' | 'connected';
  summary: HandoverSummary;
  newAgent: AgentInfo;
  reason?: string;
  urgency?: string;
}

// UI Action Type
export interface UIAction {
  action: string;
  product_ids?: string[];
  product_id?: string;
  category?: string;
  subcategory?: string;
  page?: string;
  page_number?: number;
  section?: string;
  highlight?: boolean;
  scroll_to?: boolean;
  quantity?: number;
  product_name?: string;
  query?: string;
  price?: number;
  label?: string;
  order_id?: string;
  tracking?: TrackingInfo;
  items?: OrderItem[];
  confirmation_number?: string;
  date?: string;
  time_slot?: string;
  item_name?: string;
  item_price?: number;
  address?: string;
  day?: string;
  time?: string;
  phone_number?: string;
  reason?: string;
  urgency?: string;
  summary?: HandoverSummary;
  new_agent?: AgentInfo;
}

// Message Type
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

// UI Context (for sending to backend)
export interface UIContext {
  page: string;
  category?: string;
  subcategory?: string;
  search_query?: string;
  available_subcategories?: { id: string; name: string }[];
  pagination?: {
    current_page: number;
    total_pages: number;
    products_per_page: number;
    total_products: number;
    showing: string;
  };
  cart_count: number;
  cart_items?: { id: string; name: string; price: number; quantity: number }[];
  cart_total?: number;
  visible_products?: string[];
  visible_orders?: string[];
  available_actions?: string[];
  navigation?: string[];
  highlighted_items?: string[];
  sort_by?: string;
}

// Main Context Interface
export interface VoiceContextType {
  // Connection state
  state: ConnectionState;
  chatMode: ChatMode;
  error: string | null;

  // Messages
  messages: ChatMessage[];
  currentUserText: string;
  currentAssistantText: string;
  isTyping: boolean;

  // Product highlighting
  highlightedProductIds: string[];
  selectedProduct: Product | null;

  // Audio controls
  muted: boolean;
  pushToTalkMode: boolean;
  audioDetectedWhileMuted: boolean;

  // Voice selection
  selectedVoice: GeminiVoiceId;
  setSelectedVoice: (voice: GeminiVoiceId) => void;

  // VAD mode (admin only)
  vadMode: VadMode;
  setVadMode: (mode: VadMode) => void;

  // Notifications
  trackingData: TrackingData | null;
  pickupConfirmation: PickupConfirmationData | null;
  callbackConfirmation: CallbackConfirmationData | null;
  handoverData: HandoverData | null;
  currentAgent: AgentInfo;

  // Actions
  connect: (mode?: ChatMode) => Promise<void>;
  disconnect: () => void;
  closeChat: () => void;
  clearHighlights: () => void;
  sendContext: (context: UIContext) => void;
  sendTextMessage: (text: string) => void;
  toggleMute: () => void;
  setPushToTalkMode: (enabled: boolean) => void;
  startTalking: () => void;
  stopTalking: () => void;
  dismissTracking: () => void;
  dismissPickupConfirmation: () => void;
  dismissCallbackConfirmation: () => void;
  dismissHandover: () => void;
  setSelectedProduct: (product: Product | null) => void;
  nextProductImage: () => void;
  previousProductImage: () => void;
}
